import { Component, useMemo, useState, type ErrorInfo, type ReactNode } from 'react';
import { AnalyticsWeeklySummary } from '../components/analytics/AnalyticsWeeklySummary';
import { TaskDetailModal } from '../components/dashboard/TaskDetailModal';
import { PageHeader } from '../components/ui/PageHeader';
import { PageToolbar } from '../components/ui/PageToolbar';
import { TaskFormModal } from '../components/tasks/TaskFormModal';
import { TaskTitlePieChart } from '../components/tasks/TaskTitlePieChart';
import { useCrmData } from '../contexts/CrmDataContext';
import {
  buildAccountCoverageSummaries,
  buildAccountMonthVisitSummaries,
  buildAccountVisitHistory,
  buildAnalyticsTaskDiagnostics,
  countAccountTasksInMonth,
  currentAnalyticsMonthKey,
  formatMonthKeyLabel,
  formatMonthRangeLabel,
  latestMonthKeyWithAccountTasks,
  resolveAnalyticsClient,
  rollingMonthKeys,
  shiftMonthKey,
  type AccountCoverageSummary,
  type AccountMonthVisitSummary,
} from '../lib/accountAnalytics';
import {
  analyticsTaskDayKey,
  buildCrmWeeklySummary,
  mondayOfWeekContaining,
  shiftWeekMonday,
  singaporeTodayYmd,
} from '../lib/crmWeeklySummary';
import { CRM_TASK_SAVE_SLICES } from '../lib/crmDataRefresh';
import { completeTaskAndMarkVisited } from '../lib/taskVisitationSync';
import type { Task } from '../types/crm';
import { formatSingaporeDate } from '../utils/dateTime';
import {
  clientById,
  userCanAccessClient,
  userCanWriteOwnedRecord,
} from '../utils/crmHelpers';
import { buildTaskTitlePieSlicesFromTasks } from '../utils/taskHelpers';

type CoverageSortKey = 'account' | 'salesperson' | 'status' | 'visits' | 'lastMonth';
type MonthSortKey = 'account' | 'salesperson' | 'visits';
type SortDir = 'asc' | 'desc';
type AnalyticsView = 'weekly' | 'month' | 'coverage';
type CoverageFilter = 'all' | 'met' | 'not_met';

type AnalyticsErrorBoundaryState = {
  error: Error | null;
};

class AnalyticsErrorBoundary extends Component<
  { children: ReactNode; onReset?: () => void },
  AnalyticsErrorBoundaryState
> {
  state: AnalyticsErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): AnalyticsErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Analytics page crashed', error, info);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="analytics-page">
          <PageHeader title="Analytics" description="Something went wrong loading this page." />
          <div className="alert alert-error" role="alert">
            {this.state.error.message || 'Unknown error'}
          </div>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              this.setState({ error: null });
              this.props.onReset?.();
            }}
          >
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function SortableTh({
  label,
  active,
  dir,
  onClick,
}: {
  label: string;
  active: boolean;
  dir: SortDir;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`analytics-th${active ? ' is-active' : ''}`}
      onClick={onClick}
      aria-pressed={active}
    >
      <span>{label}</span>
      <span className="analytics-th__mark" aria-hidden>
        {active ? (dir === 'asc' ? '↑' : '↓') : '↕'}
      </span>
    </button>
  );
}

function toggleSort<K extends string>(
  currentKey: K,
  currentDir: SortDir,
  nextKey: K,
  setKey: (key: K) => void,
  setDir: (dir: SortDir) => void,
) {
  if (currentKey === nextKey) {
    setDir(currentDir === 'asc' ? 'desc' : 'asc');
    return;
  }
  setKey(nextKey);
  setDir(nextKey === 'visits' || nextKey === 'lastMonth' ? 'desc' : 'asc');
}

function AnalyticsPageInner() {
  const {
    getMyData,
    data,
    ownerLabel,
    username,
    effectiveRole,
    supervisorTeam,
    actorSalespersonCodes,
    departmentSlug,
    refresh,
    markTaskDoneLocal,
  } = useCrmData();
  const tasks = useMemo(() => {
    try {
      return [...(getMyData('tasks') ?? [])];
    } catch (err) {
      console.error('Analytics tasks load failed', err);
      return [];
    }
  }, [getMyData]);
  const scopedClients = useMemo(() => {
    try {
      return [...(getMyData('clients') ?? [])];
    } catch (err) {
      console.error('Analytics clients load failed', err);
      return [];
    }
  }, [getMyData]);
  const clients = useMemo(() => data.clients ?? [], [data.clients]);
  const projects = useMemo(() => {
    try {
      return [...(getMyData('projects') ?? [])];
    } catch (err) {
      console.error('Analytics projects load failed', err);
      return [];
    }
  }, [getMyData]);
  const projectUpdates = useMemo(() => data.updates ?? [], [data.updates]);

  const [view, setView] = useState<AnalyticsView>('weekly');
  const [coverageFilter, setCoverageFilter] = useState<CoverageFilter>('not_met');
  const [monthKey, setMonthKey] = useState(() => currentAnalyticsMonthKey());
  const [weekMonday, setWeekMonday] = useState(() => mondayOfWeekContaining(singaporeTodayYmd()));
  const [search, setSearch] = useState('');
  const [coverageSortKey, setCoverageSortKey] = useState<CoverageSortKey>('account');
  const [coverageSortDir, setCoverageSortDir] = useState<SortDir>('asc');
  const [monthSortKey, setMonthSortKey] = useState<MonthSortKey>('visits');
  const [monthSortDir, setMonthSortDir] = useState<SortDir>('desc');
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [expandedMonths, setExpandedMonths] = useState<Set<string>>(() => new Set());
  const [detailTask, setDetailTask] = useState<Task | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [completing, setCompleting] = useState(false);

  const thisMonthKey = currentAnalyticsMonthKey();
  const coverageMonthKeys = useMemo(() => rollingMonthKeys(thisMonthKey, 3), [thisMonthKey]);
  const coverageRangeLabel = useMemo(
    () => formatMonthRangeLabel(coverageMonthKeys),
    [coverageMonthKeys],
  );

  const latestBusyMonth = useMemo(() => latestMonthKeyWithAccountTasks(tasks), [tasks]);
  const tasksInSelectedMonth = useMemo(
    () => countAccountTasksInMonth(tasks, monthKey),
    [tasks, monthKey],
  );
  const diagnostics = useMemo(
    () => buildAnalyticsTaskDiagnostics(tasks, monthKey),
    [tasks, monthKey],
  );

  const monthRows = useMemo(() => {
    try {
      return buildAccountMonthVisitSummaries(clients, tasks, monthKey, ownerLabel);
    } catch (err) {
      console.error('Analytics month aggregate failed', err);
      return [] as AccountMonthVisitSummary[];
    }
  }, [clients, tasks, monthKey, ownerLabel]);

  const coverageRows = useMemo(() => {
    try {
      return buildAccountCoverageSummaries(
        scopedClients,
        clients,
        tasks,
        coverageMonthKeys,
        ownerLabel,
      );
    } catch (err) {
      console.error('Analytics coverage aggregate failed', err);
      return [] as AccountCoverageSummary[];
    }
  }, [scopedClients, clients, tasks, coverageMonthKeys, ownerLabel]);

  const coverageCounts = useMemo(() => {
    let met = 0;
    let notMet = 0;
    for (const row of coverageRows) {
      if (row.met) met += 1;
      else notMet += 1;
    }
    return { met, notMet, total: coverageRows.length };
  }, [coverageRows]);

  const notVisitedRows = useMemo(
    () => coverageRows.filter((row) => !row.met),
    [coverageRows],
  );

  const overdueOpenTasks = useMemo(() => {
    const today = singaporeTodayYmd();
    let count = 0;
    for (const task of tasks) {
      const status = String(task.status ?? '')
        .trim()
        .toLowerCase();
      if (status === 'cancelled' || status === 'done' || status === 'completed') continue;
      const day = analyticsTaskDayKey(task);
      if (day && day < today) count += 1;
    }
    return count;
  }, [tasks]);

  const weeklyReport = useMemo(
    () =>
      buildCrmWeeklySummary({
        weekMonday,
        tasks,
        projects,
        updates: projectUpdates,
        clients,
        ownerLabel,
        notVisited: notVisitedRows,
        overdueOpenTasks,
      }),
    [weekMonday, tasks, projects, projectUpdates, clients, ownerLabel, notVisitedRows, overdueOpenTasks],
  );

  const thisWeekMonday = mondayOfWeekContaining(singaporeTodayYmd());

  const filteredMonthRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = monthRows;
    if (q) {
      rows = rows.filter((row) => {
        const hay = [row.companyName, row.code, row.accountSalesperson, ...row.titles]
          .join(' ')
          .toLowerCase();
        return hay.includes(q);
      });
    }
    const sorted = [...rows];
    const dir = monthSortDir === 'asc' ? 1 : -1;
    sorted.sort((a, b) => {
      let cmp = 0;
      if (monthSortKey === 'visits') cmp = a.visitCount - b.visitCount;
      else if (monthSortKey === 'salesperson') {
        cmp = a.accountSalesperson.localeCompare(b.accountSalesperson, undefined, {
          sensitivity: 'base',
        });
      } else {
        cmp = a.companyName.localeCompare(b.companyName, undefined, { sensitivity: 'base' });
      }
      if (cmp !== 0) return cmp * dir;
      return a.companyName.localeCompare(b.companyName, undefined, { sensitivity: 'base' });
    });
    return sorted;
  }, [monthRows, search, monthSortKey, monthSortDir]);

  const filteredCoverageRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = coverageRows;
    if (coverageFilter === 'met') rows = rows.filter((row) => row.met);
    else if (coverageFilter === 'not_met') rows = rows.filter((row) => !row.met);
    if (q) {
      rows = rows.filter((row) => {
        const hay = [row.companyName, row.code, row.accountSalesperson, ...row.titles]
          .join(' ')
          .toLowerCase();
        return hay.includes(q);
      });
    }
    const sorted = [...rows];
    const dir = coverageSortDir === 'asc' ? 1 : -1;
    sorted.sort((a, b) => {
      let cmp = 0;
      if (coverageSortKey === 'visits') cmp = a.visitCount - b.visitCount;
      else if (coverageSortKey === 'salesperson') {
        cmp = a.accountSalesperson.localeCompare(b.accountSalesperson, undefined, {
          sensitivity: 'base',
        });
      } else if (coverageSortKey === 'status') {
        cmp = Number(a.met) - Number(b.met);
      } else if (coverageSortKey === 'lastMonth') {
        cmp = (a.lastTaskMonthKey ?? '').localeCompare(b.lastTaskMonthKey ?? '');
      } else {
        cmp = a.companyName.localeCompare(b.companyName, undefined, { sensitivity: 'base' });
      }
      if (cmp !== 0) return cmp * dir;
      return a.companyName.localeCompare(b.companyName, undefined, { sensitivity: 'base' });
    });
    return sorted;
  }, [coverageRows, coverageFilter, search, coverageSortKey, coverageSortDir]);

  const totalVisits = useMemo(
    () => filteredMonthRows.reduce((sum, row) => sum + row.visitCount, 0),
    [filteredMonthRows],
  );

  const selectedClient = selectedClientId
    ? (resolveAnalyticsClient(clients, selectedClientId) ??
      clientById(clients, selectedClientId) ??
      null)
    : null;
  const history = useMemo(() => {
    if (!selectedClient) return null;
    try {
      return buildAccountVisitHistory(selectedClient, tasks, ownerLabel);
    } catch (err) {
      console.error('Analytics history failed', err);
      return null;
    }
  }, [selectedClient, tasks, ownerLabel]);

  const historyFlatTasks = useMemo(
    () => (history ? history.months.flatMap((month) => month.tasks) : []),
    [history],
  );
  const historyPieSlices = useMemo(
    () => buildTaskTitlePieSlicesFromTasks(historyFlatTasks.map((task) => ({ title: task.title }))),
    [historyFlatTasks],
  );

  const toggleMonth = (key: string) => {
    setExpandedMonths((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const openAccountFromMonth = (row: AccountMonthVisitSummary) => {
    setSelectedClientId(row.clientId);
    setExpandedMonths(new Set([row.monthKey]));
  };

  const openAccountFromCoverage = (row: AccountCoverageSummary) => {
    if (!row.met) return;
    setSelectedClientId(row.clientId);
    setExpandedMonths(new Set(row.lastTaskMonthKey ? [row.lastTaskMonthKey] : coverageMonthKeys));
  };

  const openAccountById = (clientId: string) => {
    setSelectedClientId(clientId);
    setExpandedMonths(new Set(coverageMonthKeys));
  };

  const openTaskDetail = (taskId: string) => {
    const task = tasks.find((row) => String(row.id) === String(taskId)) ?? null;
    if (!task) return;
    setDetailTask(task);
  };

  const canEditTask = (task: Task) =>
    userCanWriteOwnedRecord(task.owner_id, effectiveRole, username, data.users, supervisorTeam, {
      departmentSlug,
      clientId: task.client_id,
      clients: data.clients,
      actorSalespersonCodes,
    });

  const detailClient = detailTask ? clientById(data.clients, detailTask.client_id) : undefined;
  const detailCanOpenAccount = userCanAccessClient(
    detailClient,
    effectiveRole,
    username,
    data.users,
    supervisorTeam,
    actorSalespersonCodes,
    departmentSlug,
  );
  const detailMinute = useMemo(() => {
    if (!detailTask?.meeting_minute_id) return null;
    return data.meetingMinutes.find((m) => m.id === detailTask.meeting_minute_id) ?? null;
  }, [data.meetingMinutes, detailTask?.meeting_minute_id]);

  const taskDetailModals = (
    <>
      <TaskDetailModal
        open={Boolean(detailTask)}
        task={detailTask}
        client={detailClient}
        canOpenAccount={detailCanOpenAccount}
        meetingMinute={detailMinute}
        ownerName={detailTask ? ownerLabel(detailTask.owner_id) : '—'}
        canEdit={detailTask ? canEditTask(detailTask) : false}
        completing={completing}
        onClose={() => setDetailTask(null)}
        onEdit={() => {
          if (!detailTask) return;
          setEditingTask(detailTask);
          setDetailTask(null);
          setEditOpen(true);
        }}
        onDone={() => {
          void (async () => {
            if (!detailTask) return;
            setCompleting(true);
            try {
              await completeTaskAndMarkVisited(detailTask);
              markTaskDoneLocal(detailTask.id);
              void refresh({ silent: true, slices: CRM_TASK_SAVE_SLICES });
              setDetailTask(null);
            } catch (err) {
              window.alert(err instanceof Error ? err.message : 'Could not complete task.');
            } finally {
              setCompleting(false);
            }
          })();
        }}
      />
      <TaskFormModal
        open={editOpen}
        task={editingTask}
        onClose={() => {
          setEditOpen(false);
          setEditingTask(null);
        }}
      />
    </>
  );

  if (selectedClientId && selectedClient && history) {
    return (
      <div className="analytics-page">
        <header className="page-header">
          <div className="page-header__text">
            <h1 className="page-title">{history.companyName}</h1>
            <p className="analytics-page__account-code mono">{history.code}</p>
            <p className="page-lead">
              Salesperson Code {history.accountSalesperson} · {history.totalVisits} task
              {history.totalVisits === 1 ? '' : 's'}
            </p>
          </div>
          <div className="page-header__actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setSelectedClientId(null);
                setExpandedMonths(new Set());
                setDetailTask(null);
              }}
            >
              ← Back
            </button>
          </div>
        </header>

        {history.months.length === 0 ? (
          <p className="analytics-page__empty">No dated tasks for this account in your scope.</p>
        ) : (
          <div className="analytics-history__split">
            <div className="analytics-history__pie card">
              <TaskTitlePieChart
                monthLabel="Task titles"
                total={historyFlatTasks.length}
                slices={historyPieSlices}
                embedded
              />
            </div>
            <div className="analytics-history">
              {history.months.map((month) => {
                const open = expandedMonths.has(month.monthKey);
                return (
                  <section key={month.monthKey} className="analytics-history__month card">
                    <button
                      type="button"
                      className="analytics-history__month-head"
                      onClick={() => toggleMonth(month.monthKey)}
                      aria-expanded={open}
                    >
                      <span className="analytics-history__month-label">
                        {formatMonthKeyLabel(month.monthKey)}
                      </span>
                      <span className="analytics-history__month-meta">
                        {month.visitCount} task{month.visitCount === 1 ? '' : 's'}
                      </span>
                      <span className="analytics-history__chevron" aria-hidden>
                        {open ? '▾' : '▸'}
                      </span>
                    </button>
                    {open ? (
                      <div className="analytics-history__body">
                        <ul className="analytics-history__tasks">
                          {month.tasks.map((task) => (
                            <li key={task.taskId}>
                              <button
                                type="button"
                                className="analytics-history__task"
                                onClick={() => openTaskDetail(task.taskId)}
                              >
                                <span className="analytics-history__task-line">
                                  {task.title} on {formatSingaporeDate(task.dueDate)}
                                </span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </section>
                );
              })}
            </div>
          </div>
        )}
        {taskDetailModals}
      </div>
    );
  }

  if (selectedClientId && !selectedClient) {
    return (
      <div className="analytics-page">
        <PageHeader
          title="Analytics"
          description="Account not found in your current scope."
          actions={
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setSelectedClientId(null)}
            >
              ← Back
            </button>
          }
        />
      </div>
    );
  }

  return (
    <div className="analytics-page">
      <PageHeader
        title="Analytics"
        description="Weekly visit summary, month detail, and 3-month coverage — scoped by the top-bar User filter."
      />

      <div className="analytics-page__view-toggle" role="tablist" aria-label="Analytics view">
        <button
          type="button"
          role="tab"
          aria-selected={view === 'weekly'}
          className={`analytics-page__view-btn${view === 'weekly' ? ' is-active' : ''}`}
          onClick={() => setView('weekly')}
        >
          Weekly summary
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={view === 'coverage'}
          className={`analytics-page__view-btn${view === 'coverage' ? ' is-active' : ''}`}
          onClick={() => setView('coverage')}
        >
          Coverage (3 mo)
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={view === 'month'}
          className={`analytics-page__view-btn${view === 'month' ? ' is-active' : ''}`}
          onClick={() => setView('month')}
        >
          Month
        </button>
      </div>

      <PageToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by account, code or salesperson…"
        action={
          <div className="analytics-page__toolbar-actions">
            {view === 'weekly' ? (
              <div className="analytics-page__month-nudge">
                <span className="analytics-page__week-label" title={weeklyReport.weekLabel}>
                  {weeklyReport.weekLabel}
                </span>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setWeekMonday((current) => shiftWeekMonday(current, -1))}
                >
                  ‹ Prev week
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setWeekMonday(thisWeekMonday)}
                >
                  This week
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setWeekMonday((current) => shiftWeekMonday(current, 1))}
                >
                  Next week ›
                </button>
              </div>
            ) : view === 'month' ? (
              <>
                <label className="analytics-page__month-field">
                  <span className="field-label">Month</span>
                  <input
                    className="field-input"
                    type="month"
                    value={monthKey}
                    onChange={(e) => setMonthKey(e.target.value || currentAnalyticsMonthKey())}
                  />
                </label>
                <div className="analytics-page__month-nudge">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setMonthKey((current) => shiftMonthKey(String(current), -1))}
                  >
                    ‹ Prev
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setMonthKey(currentAnalyticsMonthKey())}
                  >
                    This month
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setMonthKey((current) => shiftMonthKey(String(current), 1))}
                  >
                    Next ›
                  </button>
                </div>
              </>
            ) : (
              <div className="analytics-page__coverage-filter" role="group" aria-label="Coverage filter">
                {(
                  [
                    ['not_met', 'Not visited'],
                    ['met', 'Visited'],
                    ['all', 'All'],
                  ] as const
                ).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    className={`analytics-page__segment-btn${coverageFilter === value ? ' is-active' : ''}`}
                    onClick={() => setCoverageFilter(value)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>
        }
      />

      {view === 'weekly' ? (
        <>
          <AnalyticsWeeklySummary
            report={weeklyReport}
            search={search}
            onOpenAccount={openAccountById}
            onOpenTask={openTaskDetail}
          />
          {taskDetailModals}
        </>
      ) : view === 'coverage' ? (
        <>
          <p className="analytics-page__summary">
            {coverageRangeLabel} · {coverageCounts.met} visited · {coverageCounts.notMet} not
            visited · {coverageCounts.total} account{coverageCounts.total === 1 ? '' : 's'}
          </p>

          {filteredCoverageRows.length === 0 ? (
            <div className="analytics-page__empty-panel">
              <p className="analytics-page__empty">
                {scopedClients.length === 0
                  ? 'No accounts in your current scope (check top-bar User filter).'
                  : coverageFilter === 'not_met'
                    ? 'Every account in scope has at least one task in this 3-month window.'
                    : coverageFilter === 'met'
                      ? 'No visited accounts match this search.'
                      : 'No accounts match this search.'}
              </p>
            </div>
          ) : (
            <div className="card analytics-table-wrap analytics-table-wrap--coverage">
              <div className="analytics-coverage__head" role="row">
                <SortableTh
                  label="Account"
                  active={coverageSortKey === 'account'}
                  dir={coverageSortDir}
                  onClick={() =>
                    toggleSort(
                      coverageSortKey,
                      coverageSortDir,
                      'account',
                      setCoverageSortKey,
                      setCoverageSortDir,
                    )
                  }
                />
                <SortableTh
                  label="Salesperson Code"
                  active={coverageSortKey === 'salesperson'}
                  dir={coverageSortDir}
                  onClick={() =>
                    toggleSort(
                      coverageSortKey,
                      coverageSortDir,
                      'salesperson',
                      setCoverageSortKey,
                      setCoverageSortDir,
                    )
                  }
                />
                <SortableTh
                  label="Status"
                  active={coverageSortKey === 'status'}
                  dir={coverageSortDir}
                  onClick={() =>
                    toggleSort(
                      coverageSortKey,
                      coverageSortDir,
                      'status',
                      setCoverageSortKey,
                      setCoverageSortDir,
                    )
                  }
                />
                <SortableTh
                  label="Visits"
                  active={coverageSortKey === 'visits'}
                  dir={coverageSortDir}
                  onClick={() =>
                    toggleSort(
                      coverageSortKey,
                      coverageSortDir,
                      'visits',
                      setCoverageSortKey,
                      setCoverageSortDir,
                    )
                  }
                />
                <SortableTh
                  label="Last month"
                  active={coverageSortKey === 'lastMonth'}
                  dir={coverageSortDir}
                  onClick={() =>
                    toggleSort(
                      coverageSortKey,
                      coverageSortDir,
                      'lastMonth',
                      setCoverageSortKey,
                      setCoverageSortDir,
                    )
                  }
                />
              </div>
              <ul className="analytics-table__list">
                {filteredCoverageRows.map((row) => (
                  <li key={row.clientId}>
                    <button
                      type="button"
                      className={`analytics-coverage__row${row.met ? '' : ' analytics-coverage__row--muted'}`}
                      onClick={() => openAccountFromCoverage(row)}
                      disabled={!row.met}
                    >
                      <span className="analytics-table__account">
                        <span className="analytics-table__name">{row.companyName}</span>
                        <span className="analytics-table__account-code mono">{row.code}</span>
                      </span>
                      <span className="analytics-table__sp mono">{row.accountSalesperson}</span>
                      <span
                        className={`analytics-coverage__status${
                          row.met
                            ? ' analytics-coverage__status--met'
                            : ' analytics-coverage__status--not-met'
                        }`}
                      >
                        {row.met ? 'Visited' : 'Not visited'}
                      </span>
                      <span className="analytics-table__count">{row.visitCount}</span>
                      <span className="analytics-coverage__last">
                        {row.lastTaskMonthKey ? formatMonthKeyLabel(row.lastTaskMonthKey) : '—'}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      ) : (
        <>
          <p className="analytics-page__summary">
            {formatMonthKeyLabel(monthKey)} · {filteredMonthRows.length} account
            {filteredMonthRows.length === 1 ? '' : 's'} · {totalVisits} task
            {totalVisits === 1 ? '' : 's'}
            {diagnostics.scopedTasks > 0
              ? ` · scope ${diagnostics.scopedTasks} task${diagnostics.scopedTasks === 1 ? '' : 's'}`
              : ''}
          </p>

          {filteredMonthRows.length === 0 ? (
            <div className="analytics-page__empty-panel">
              <p className="analytics-page__empty">
                {diagnostics.scopedTasks === 0
                  ? 'No tasks in your current scope (check top-bar User filter).'
                  : diagnostics.withMonthKey === 0
                    ? 'Tasks are loaded, but none have a usable due/created date for month grouping.'
                    : tasksInSelectedMonth === 0
                      ? `No tasks fall in ${formatMonthKeyLabel(monthKey)} (${diagnostics.withMonthKey} dated task${
                          diagnostics.withMonthKey === 1 ? '' : 's'
                        } in scope).`
                      : 'No matching accounts for this month after search/filters.'}
              </p>
              <div className="analytics-page__empty-actions">
                {monthKey !== thisMonthKey ? (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => setMonthKey(thisMonthKey)}
                  >
                    Go to {formatMonthKeyLabel(thisMonthKey)}
                  </button>
                ) : null}
                {latestBusyMonth && latestBusyMonth !== monthKey ? (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setMonthKey(latestBusyMonth)}
                  >
                    Latest with data ({formatMonthKeyLabel(latestBusyMonth)})
                  </button>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="card analytics-table-wrap">
              <div className="analytics-table__head" role="row">
                <SortableTh
                  label="Account"
                  active={monthSortKey === 'account'}
                  dir={monthSortDir}
                  onClick={() =>
                    toggleSort(monthSortKey, monthSortDir, 'account', setMonthSortKey, setMonthSortDir)
                  }
                />
                <SortableTh
                  label="Salesperson Code"
                  active={monthSortKey === 'salesperson'}
                  dir={monthSortDir}
                  onClick={() =>
                    toggleSort(
                      monthSortKey,
                      monthSortDir,
                      'salesperson',
                      setMonthSortKey,
                      setMonthSortDir,
                    )
                  }
                />
                <SortableTh
                  label="Visits"
                  active={monthSortKey === 'visits'}
                  dir={monthSortDir}
                  onClick={() =>
                    toggleSort(monthSortKey, monthSortDir, 'visits', setMonthSortKey, setMonthSortDir)
                  }
                />
              </div>
              <ul className="analytics-table__list">
                {filteredMonthRows.map((row) => (
                  <li key={row.clientId}>
                    <button
                      type="button"
                      className="analytics-table__row"
                      onClick={() => openAccountFromMonth(row)}
                    >
                      <span className="analytics-table__account">
                        <span className="analytics-table__name">{row.companyName}</span>
                        <span className="analytics-table__account-code mono">{row.code}</span>
                      </span>
                      <span className="analytics-table__sp mono">{row.accountSalesperson}</span>
                      <span className="analytics-table__count">{row.visitCount}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function AnalyticsPage() {
  return (
    <AnalyticsErrorBoundary>
      <AnalyticsPageInner />
    </AnalyticsErrorBoundary>
  );
}
